import { easeOutBounce } from "@huub-web/utils";

const initialReset = {
  rotate: 0,
  x: 0,
  y: 0,
  scale: 1,
  transition: {
    duration: 1,
  },
};

export enum HuubAvatarEvents {
  AVATAR_AUDIO_EVENT = "avatarAudioEvent",
}

export const ANIMATION_PADDING = 40;

export const AVATAR_ANIMATIONS = {
  idle: {
    initial: {
      ...initialReset,
    },
    animate: {
      rotate: 0,
      x: 0,
      y: 0,
      scale: 1,
      transition: {
        duration: 1,
      },
    },
  },
  swing: {
    initial: {
      ...initialReset,
      rotate: 25,
    },
    animate: {
      rotate: [25, -25],
      transition: {
        repeat: Infinity,
        repeatType: "mirror" as const,
        duration: 2,
        delayPerLayer: 0.1,
      },
    },
  },
  moveHorizontal: {
    initial: {
      ...initialReset,
      x: -ANIMATION_PADDING,
    },
    animate: {
      x: [-ANIMATION_PADDING, ANIMATION_PADDING],
      transition: {
        repeat: Infinity,
        repeatType: "mirror" as const,
        duration: 2,
        ease: "easeInOut" as const,
        delayPerLayer: 0.06,
      },
    },
  },
  moveVertical: {
    initial: {
      ...initialReset,
      y: -ANIMATION_PADDING,
    },
    animate: {
      y: [-ANIMATION_PADDING, ANIMATION_PADDING],
      transition: {
        duration: 2,
        ease: "easeInOut" as const,
        repeat: Infinity,
        repeatType: "mirror" as const,
        delayPerLayer: 0.06,
      },
    },
  },
  bounceOut: {
    initial: {
      ...initialReset,
      scale: 1.001,
    },
    animate: {
      scale: [1, 1.2, 1],
      transition: {
        repeat: Infinity,
        repeatType: "reverse" as const,
        duration: 3,
        delayPerLayer: 0.1,
        ease: [0.83, 0, 0.17, 1] as const,
      },
    },
  },
  bounceIn: {
    initial: {
      ...initialReset,
      scale: 1.0011,
    },
    animate: {
      scale: [1, 0.8, 1],
      transition: {
        repeat: Infinity,
        repeatType: "reverse" as const,
        duration: 3,
        delayPerLayer: 0.1,
      },
    },
  },
  spin: {
    initial: {
      ...initialReset,
      rotate: 0.01,
    },
    animate: {
      rotate: [0, 90],
      transition: {
        repeat: Infinity,
        duration: 3,
        delayPerLayer: 0.05,
      },
    },
  },
  revolve: {
    initial: {
      ...initialReset,
      x: -ANIMATION_PADDING,
      y: -ANIMATION_PADDING,
    },
    animate: {
      x: [
        -ANIMATION_PADDING,
        ANIMATION_PADDING,
        ANIMATION_PADDING,
        -ANIMATION_PADDING,
        -ANIMATION_PADDING,
      ],
      y: [
        -ANIMATION_PADDING,
        -ANIMATION_PADDING,
        ANIMATION_PADDING,
        ANIMATION_PADDING,
        -ANIMATION_PADDING,
      ],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut" as const,
        delayPerLayer: 0.05,
      },
    },
  },
  layerRotate: {
    initial: {
      ...initialReset,
      rotate: 0.0001,
    },
    animate: ({ index }: { index: number }) => ({
      rotate: 90 * (index % 2 === 0 ? -1 : 1),
      transition: {
        duration: 3,
        repeat: Infinity,
      },
    }),
  },
  alternateSpin: {
    initial: {
      ...initialReset,
      rotate: 0.0001,
    },
    animate: ({ index }: { index: number }) => ({
      rotate: index > 0 ? 90 * (index % 2 === 0 ? -1 : 1) : 0,
      transition: {
        duration: 5,
        repeat: Infinity,
        ease: "linear" as const,
      },
    }),
  },
  shake: {
    initial: {
      ...initialReset,
      rotate: 0.001, // we add this so there is a difference for the tween
    },
    animate: {
      x: [10, -5, 10, -10, 5, -10, 5, -5, 0],
      transition: {
        repeat: Infinity,
        repeatType: "reverse" as const,
        repeatDelay: 2,
        duration: 1,
      },
    },
  },
  nod: {
    initial: {
      ...initialReset,
      rotate: 0.0011, // we add this so there is a difference for the tween
    },
    animate: {
      y: [10, -8, 10, -10, 3, -10, 3, -3, 0],
      transition: {
        repeat: Infinity,
        repeatDelay: 2,
        duration: 2,
        ease: [0.34, 1.56, 0.64, 1] as const,
      },
    },
  },
  weave: {
    initial: {
      ...initialReset,
      x: 0.0001,
      y: 0.0001,
    },
    animate: ({
      index,
      layerCount,
      size,
    }: {
      index: number;
      layerCount: number;
      size: number;
    }) => {
      const div = size / layerCount / 2;

      return {
        x: index % 2 === 0 ? [0, -div, div, 0, -div, div] : 0,
        y: index % 2 > 0 ? [0, -div, div, 0, div, -div] : 0,
        transition: {
          duration: 7,
          repeat: Infinity,
          repeatType: "mirror" as const,
          ease: [0.25, 1, 0.5, 1] as const,
        },
      };
    },
  },
  drop: {
    initial: {
      ...initialReset,
      y: 0.0001,
      x: 0.0001,
    },
    animate: ({
      index,
      layerCount,
      size,
    }: {
      index: number;
      layerCount: number;
      size: number;
    }) => {
      const div = size / layerCount / 2;

      return {
        y: index > 0 ? [0, div * index] : 0,
        transition: {
          duration: 2,
          repeat: 0,
          ease: easeOutBounce,
        },
      };
    },
  },
  float: {
    initial: {
      ...initialReset,
      y: 0.0001,
      x: 0.0001,
    },
    animate: ({
      index,
      layerCount,
      size,
    }: {
      index: number;
      layerCount: number;
      size: number;
    }) => {
      const div = size / layerCount / 2;

      return {
        y: index > 0 ? [0, -div * index] : 0,
        transition: {
          duration: 5,
          repeat: 0,
          ease: easeOutBounce,
        },
      };
    },
  },
} as const;
